// Adapter that lets RTK Query drive the shared axios instance. Reads the
// right auth token from state (via api.getState, no store import), attaches
// it, unwraps the { success, data, error } envelope, and mirrors the
// `custom-notifications` unread count into the store. On a 401 it
// transparently refreshes the session that request belonged to — once — and
// retries; concurrent 401s share a single refresh call (see refreshSession).
// See REFRESHABLE below for the two sessions and their endpoints.

import type { BaseQueryApi, BaseQueryFn } from '@reduxjs/toolkit/query';
import type { RootState } from '@/store/store';
import { setAuth, clearAuth, type AuthState } from '@/redux/auth/authSlice';
import { setUnreadCount } from '@/redux/notifications/notificationsSlice';
import { client } from './client';
import { readUnreadNotifications } from './notificationsHeader';
import { ROUTES } from './routes';
import { ApiError, type ApiErrorShape, type HttpMethod, type TokenType } from './types';
// Type-only: the runtime cycle companyApi → baseApi → this file never closes.
import type { SelectCompanyResult } from './endpoints/companyApi';

export interface AxiosBaseQueryArgs {
  url: string;
  method?: HttpMethod;
  data?: unknown;
  params?: unknown;
  /** Which token to attach — required, no default: every route descriptor in
   *  routes.ts declares its session explicitly, so a silent authToken
   *  assumption can't creep in. */
  tokenType: TokenType;
}

type QueryResult = { data: unknown } | { error: ApiErrorShape };

/** `refresh-token/user` returns only the login tokens; `refresh-token/company`
 *  returns the full company session — the same `SelectCompanyResult` body
 *  select-company sends. */
interface RefreshUserTokens {
  token: string;
  refreshToken: string;
}

/** Which endpoint renews which session, and where its new session data lands.
 *
 *  Only these two token kinds are renewable: `staticToken`, `validationKey` and
 *  `external` calls have no session behind them, so a 401 on one is final. The
 *  refresh request itself goes through `rawQuery`, never through the wrapper
 *  below, so it can't recurse into another refresh. */
const REFRESHABLE = {
  authToken: {
    route: ROUTES.auth.refreshCompany,
    // Stores the WHOLE session, mirroring useSelectCompany's setAuth
    // field-for-field (`company` lands as `companyDetail`), so sessions
    // persisted before a field existed (e.g. `companyUserId`) heal on their
    // first refresh. `ownerCompanyUserId` isn't in this response and is left
    // untouched.
    store: (t: SelectCompanyResult): Partial<AuthState> => ({
      authToken: t.token,
      refreshToken: t.refreshToken,
      companyId: t.companyId,
      companyDetail: t.company,
      roles: t.roles,
      permissions: t.permissions,
      user: t.user,
      companyUserId: t.companyUserId,
    }),
  },
  loginToken: {
    route: ROUTES.auth.refreshUser,
    store: (t: RefreshUserTokens): Partial<AuthState> => ({
      loginToken: t.token,
      loginRefreshToken: t.refreshToken,
    }),
  },
} as const;

const pickToken = (state: RootState, tokenType: TokenType): string => {
  if (tokenType === 'staticToken') return import.meta.env.VITE_STATIC_TOKEN ?? '';
  if (tokenType === 'external') return '';
  // Strictly the requested session's token — never substitute another. A
  // missing token sends the request bare (no Authorization header), which
  // 401s cleanly into the refresh/clearAuth path instead of a confusing
  // wrong-session failure.
  return state.auth?.[tokenType] ?? '';
};

const rawQuery = async (
  { url, method = 'GET', data, params, tokenType }: AxiosBaseQueryArgs,
  api: BaseQueryApi,
): Promise<QueryResult> => {
  try {
    const token = pickToken(api.getState() as RootState, tokenType);
    const response = await client({
      url,
      method,
      data,
      params,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    // Every company-session response carries the live unread count in its
    // `custom-notifications` header (the decrypt interceptor only touches
    // `response.data`, so headers arrive intact). Only the authToken session
    // has a meaningful count — login/static/refresh/external calls are
    // skipped — and the store is written only when the number changed.
    if (tokenType === 'authToken') {
      const unread = readUnreadNotifications(response.headers);
      if (
        unread !== undefined &&
        unread !== (api.getState() as RootState).notifications.unreadCount
      ) {
        api.dispatch(setUnreadCount(unread));
      }
    }
    const body = response.data;
    if (body && body.success === false) {
      // `error` is an object (its `msg`) or null here — the string-array
      // shape only arrives on HTTP-error responses (see client.ts). The
      // envelope's `message` is success-only and never consulted. The object
      // passes through as `data` so features can branch on structured
      // payloads (e.g. the ended-workflow rejection's `{flowState}`).
      return {
        error: {
          status: response.status,
          msg: body.error?.msg ?? 'Request failed',
          data: body.error ?? undefined,
        },
      };
    }
    return { data: body?.data ?? body };
  } catch (e) {
    const err = e as ApiError;
    return { error: { status: err.status, msg: err.msg ?? 'Network error' } };
  }
};

type RefreshableType = keyof typeof REFRESHABLE;

/** The refresh currently running for each session, if any. Every request that
 *  401s while a refresh is in flight awaits this same promise instead of
 *  starting its own, so a burst of parallel 401s costs exactly one refresh
 *  call — firing several would also race the rotating refresh token, and the
 *  losers would clear a session the winner had just renewed. */
const inflightRefresh: Partial<Record<RefreshableType, Promise<boolean>>> = {};

/** Renews `tokenType`'s session, sharing one in-flight call between all
 *  callers. Resolves true when new tokens were stored, false when the session
 *  is gone (auth already cleared). Never rejects: rawQuery folds failures into
 *  `{ error }`. */
const refreshSession = (tokenType: RefreshableType, api: BaseQueryApi): Promise<boolean> => {
  const pending = inflightRefresh[tokenType];
  if (pending) return pending;

  const refresh = REFRESHABLE[tokenType];
  const run = (async () => {
    const refreshed = await rawQuery({ ...refresh.route }, api);
    const tokens =
      'data' in refreshed
        ? (refreshed.data as Partial<SelectCompanyResult> | undefined)
        : undefined;
    // Guard on the TOKEN, not just a truthy body: setAuth is Object.assign, so
    // a 200 carrying `{}` or an unexpected shape would write `undefined` over
    // the live token, and the retries would then run unauthenticated and
    // leave the app half-signed-in. No token means the session is gone.
    if (tokens?.token) {
      api.dispatch(setAuth(refresh.store(tokens as SelectCompanyResult)));
      return true;
    }
    api.dispatch(clearAuth());
    return false;
  })().finally(() => {
    delete inflightRefresh[tokenType];
  });

  inflightRefresh[tokenType] = run;
  return run;
};

export const axiosBaseQuery =
  (): BaseQueryFn<AxiosBaseQueryArgs, unknown, ApiErrorShape> => async (args, api, _extra) => {
    // Remember which token this attempt carried, to tell a stale 401 (a
    // refresh already landed while it was in flight) from an expired session.
    const sentToken = pickToken(api.getState() as RootState, args.tokenType);
    const result = await rawQuery(args, api);

    if (!('error' in result) || result.error.status !== 401) return result;
    if (!(args.tokenType in REFRESHABLE)) return result;
    const tokenType = args.tokenType as RefreshableType;

    // Token changed since we sent: another request's refresh already finished,
    // so just retry with the new one rather than refreshing again with a
    // refresh token that may have been rotated away.
    const currentToken = pickToken(api.getState() as RootState, tokenType);
    // Token gone since we sent: a sibling's refresh already failed and cleared
    // the session, so a bare refresh would only 401 again — surface the error.
    if (sentToken !== '' && currentToken === '') return result;
    const alreadyRefreshed = currentToken !== '' && currentToken !== sentToken;

    if (alreadyRefreshed || (await refreshSession(tokenType, api))) {
      return rawQuery(args, api); // retry the original once
    }
    return result;
  };
